import { useState, useCallback } from 'react';
import { login as loginService } from '../services/authService';

/**
 * useLogin hook
 * Encapsulates all login form state, validation, and submission logic.
 * The LoginPage component stays a pure "view" — this hook owns the behaviour.
 *
 * @returns {{
 *   form: object,
 *   errors: object,
 *   globalError: string,
 *   isLoading: boolean,
 *   showPassword: boolean,
 *   handleChange: Function,
 *   handleSubmit: Function,
 *   togglePassword: Function,
 * }}
 */
export function useLogin(onSuccess) {
  const [form, setForm] = useState({ phone: '', password: '' });
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /** Validate form fields and return a map of field → error message. */
  const validate = (values) => {
    const e = {};
    if (!values.phone.trim()) {
      e.phone = 'Phone number is required.';
    }
    if (!values.password) {
      e.password = 'Password is required.';
    } else if (values.password.length < 4) {
      e.password = 'Password must be at least 4 characters.';
    }
    return e;
  };

  /** Map HTTP status codes to user-friendly messages. */
  const resolveErrorMessage = (err) => {
    const status = err?.response?.status;
    const serverMsg = err?.response?.data?.message || err?.response?.data;

    if (err.message?.startsWith('Access denied')) return err.message;
    if (status === 401) return 'Invalid phone number or password. Please try again.';
    if (status === 403) return 'Your account has been suspended. Contact support.';
    if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (!err.response) return 'Cannot reach the server. Check your internet connection.';
    return typeof serverMsg === 'string' ? serverMsg : 'An unexpected error occurred.';
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
    setGlobalError('');
  }, []);

  const togglePassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const validationErrors = validate(form);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsLoading(true);
      setGlobalError('');

      try {
        const data = await loginService(form);
        onSuccess(data);
      } catch (err) {
        setGlobalError(resolveErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [form, onSuccess]
  );

  return {
    form,
    errors,
    globalError,
    isLoading,
    showPassword,
    handleChange,
    handleSubmit,
    togglePassword,
  };
}
